import { push } from 'connected-react-router';
import { Polygon } from 'geojson';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { ERROR_ACTION, WAIT_FOR_ACTION } from 'redux-wait-for-action';
import { mapBoundsSelector, querySelector } from '../store/app/selectors';
import { userSettingsSelector } from '../store/auth/selectors';
import { channelByIdSelector } from '../store/channels/selectors';
import { observation } from '../store/observations/actions';
import { Observation, ObservationSettings } from '../store/observations/types';
import { satellitesWithTilesSelector, selectedSatellitesSelector } from '../store/satellites/selectors';
import { mapBoundsToBboxPolygon } from '../utils/geo';

export interface useCreateObservationOptions {
  channel: number | null;
  name: string;
  satellites: number[];
  geometry: Polygon;
  settings: Partial<ObservationSettings>;
}

export const createObservation = (dispatch: Dispatch) => {
  return (
    channel: number | null,
    name: string,
    satellites: number[],
    geometry: Polygon,
    settings: Partial<ObservationSettings>,
  ): any => {
    return dispatch({
      ...observation.post.request({ channel, name, satellites, geometry, settings }),
      [WAIT_FOR_ACTION]: observation.post.SUCCESS,
      [ERROR_ACTION]: observation.post.FAILURE,
    });
  };
};

export default function useCreateObservation() {
  const mapBounds = useSelector(mapBoundsSelector);
  const userSettings = useSelector(userSettingsSelector);
  const channelById = useSelector(channelByIdSelector);
  const selectedSatellites = useSelector(selectedSatellitesSelector);
  const satellitesWithTiles = useSelector(satellitesWithTilesSelector);
  const query = useSelector(querySelector);

  const { t } = useTranslation(['common']);
  const dispatch = useDispatch();

  const observationGeometry = mapBoundsToBboxPolygon(mapBounds);
  let channelId: number | null = null;

  if (userSettings.activeChannelId) {
    const channel = channelById(userSettings.activeChannelId);
    channelId = channel && channel.can_edit ? channel.id : null;
  }

  const observationSatellites = selectedSatellites.length > 0 ? selectedSatellites : satellitesWithTiles;
  const satellitesIds = observationSatellites.map((satellite) => satellite.id);

  const defaultObservationSettings = {
    imageriesDisplay: userSettings.defaultImageriesDisplay,
  };

  const create = createObservation(dispatch);

  const createAndPush = (
    options: useCreateObservationOptions = {
      channel: channelId,
      name: query || t('common:observation'),
      satellites: satellitesIds,
      geometry: observationGeometry,
      settings: defaultObservationSettings,
    },
  ) => {
    const { name, satellites, geometry, channel } = options;
    const mergedSettings = { ...defaultObservationSettings, ...options.settings };

    create(channel, name, satellites, geometry, mergedSettings).then((observation: Observation) => {
      dispatch(push(`/observation/${observation.uuid}`));
    });
  };

  return createAndPush;
}
